import { Injectable, inject } from '@angular/core';

import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import {
  map,
  switchMap,
  startWith,
  finalize,
  take,
  withLatestFrom,
  tap,
  concatMap,
  delay,
} from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { AppActionTypes } from 'src/app/store/actions';

import { getCropYear } from 'src/app/store/selectors';

import * as SoilAnalysisActions from './soil-analysis.actions';
import {
  selectLatestSoilAnalysisByFeatures,
  selectSoilAnalysisByFeatureId,
} from './soil-analysis.selectors';

import { ManualSoilAnalysisDialogComponent } from '../components/manual-soil-analysis-dialog/manual-soil-analysis-dialog.component';
import { SoilAnalysisDialogComponent } from '../components/soil-analysis-dialog/soil-analysis-dialog.component';
import { SoilAnalysisTitleHelper } from '../helpers/SoilAnalysisTitleHelper';
import { SoilAnalysisApiService } from '../services/soil-analysis-api.service';

@Injectable()
export class SoilAnalysisEffects {
  private readonly actions$ = inject(Actions);
  private readonly soilAnalysisApi = inject(SoilAnalysisApiService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly router = inject(Router);

  getCustomerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActionTypes.GetCustomerData),
      map(() => SoilAnalysisActions.getSoilAnalyses()),
    ),
  );

  getSoilAnalyses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.getSoilAnalyses),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      switchMap(([_, cropYear]) =>
        this.soilAnalysisApi.fetchSoilAnalyses(cropYear).pipe(
          map((soilAnalyses) => SoilAnalysisActions.setSoilAnalyses({ soilAnalyses })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  initEditManualSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.initEditManualSoilAnalysis),
      switchMap(({ id }) =>
        this.soilAnalysisApi.fetchManualSoilAnalysis(id).pipe(
          map((manualSoilAnalysis) =>
            SoilAnalysisActions.editManualSoilAnalysis({ manualSoilAnalysis }),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  editManualSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.editManualSoilAnalysis),
      switchMap(({ manualSoilAnalysis }) =>
        this.dialog
          .form(ManualSoilAnalysisDialogComponent, {
            manualSoilAnalysis,
            pushHistoryState: false,
            title:
              manualSoilAnalysis.id === 0
                ? this.locale.translate('addManualSoilAnalysis')
                : this.locale.translate('editManualSoilAnalysis'),
          })
          .pipe(
            switchMap(({ status, result }) =>
              status === DIALOG_RETURN_TYPES.confirm
                ? of(
                    SoilAnalysisActions.upsertManualSoilAnalysis({
                      manualSoilAnalysis: {
                        ...result,
                      },
                    }),
                  )
                : EMPTY,
            ),
          ),
      ),
    ),
  );

  upsertManualSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.upsertManualSoilAnalysis),
      switchMap(({ manualSoilAnalysis: soilAnalysis }) =>
        this.soilAnalysisApi.upsertManualSoilAnalysis(soilAnalysis).pipe(
          delay(500),
          concatMap((id) => [
            SoilAnalysisActions.soilAnalysisUpserted({
              id,
            }),
            SoilAnalysisActions.getSoilAnalyses(),
          ]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  selectSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.selectSoilAnalysis),
      map(({ soilAnalysis }) => SoilAnalysisActions.showSoilAnalysis({ soilAnalysis })),
    ),
  );

  selectLatestSoilAnalysisByFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.selectMultipleSoilAnalysisFeatures),
      switchMap(({ featureIds }) =>
        this.store.pipe(select(selectLatestSoilAnalysisByFeatures({ featureIds })), take(1)),
      ),
      map((soilAnalysis) => SoilAnalysisActions.showSoilAnalysis({ soilAnalysis })),
    ),
  );

  selectSoilAnalysisFeature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.selectSoilAnalysisFeature),
      switchMap(({ featureId }) =>
        this.store.pipe(select(selectSoilAnalysisByFeatureId({ featureId })), take(1)),
      ),
      map((soilAnalysis) => SoilAnalysisActions.showSoilAnalysis({ soilAnalysis })),
    ),
  );

  showSoilAnalysis$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.showSoilAnalysis),
      switchMap(({ soilAnalysis }) =>
        this.dialog
          .fullscreenDialog(SoilAnalysisDialogComponent, {
            soilAnalysisId: soilAnalysis.id,
            title: SoilAnalysisTitleHelper.getSoilAnalysisTitle(soilAnalysis),
            cancelButton: false,
            confirmButton: soilAnalysis.isManualEntry,
            confirmButtonText: this.locale.translate('editManualSoilAnalysis'),
            altButton: true,
            altButtonIcon: 'link',
            altButtonColor: 'primary',
            altButtonText: this.locale.translate('linkSoilAnalysisToCropField'),
          })
          .pipe(
            switchMap(({ status }) => {
              if (status === DIALOG_RETURN_TYPES.alt) {
                return of(SoilAnalysisActions.linkCropField({ soilAnalysisId: soilAnalysis.id }));
              } else if (status === DIALOG_RETURN_TYPES.confirm) {
                return of(SoilAnalysisActions.initEditManualSoilAnalysis({ id: soilAnalysis.id }));
              } else {
                if (soilAnalysis.borderData?.id) {
                  return of(
                    SoilAnalysisActions.deselectSoilAnalysisFeature({
                      featureId: soilAnalysis.borderData?.id,
                    }),
                  );
                } else {
                  return EMPTY;
                }
              }
            }),
          ),
      ),
    ),
  );

  openSoilAnalysisReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SoilAnalysisActions.openSoilAnalysesReport),
        tap(({ cropFieldIds, characteristicIds = [] }) => {
          const cropFields = JSON.stringify(cropFieldIds);
          if (characteristicIds.length) {
            const characteristics = JSON.stringify(characteristicIds);
            return this.router.navigate(['soil-analysis/report', cropFields, characteristics]);
          }
          return this.router.navigate(['soil-analysis/report', cropFields]);
        }),
      ),
    { dispatch: false },
  );

  fetchDefaultSoilAnalyses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilAnalysisActions.fetchDefaultSoilAnalyses),
      switchMap(() =>
        this.soilAnalysisApi.fetchDefaultSoilAnalyses().pipe(
          map((soilAnalyses) => SoilAnalysisActions.setDefaultSoilAnalyses({ soilAnalyses })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
